/**
 * OAuth new-tab bridge — shared channel + the original-tab listener.
 *
 * The connect flow opens the provider's consent screen in a NEW TAB
 * (see useConnectPlatform). After consent the BE redirects that tab to the
 * `/oauth/callback` route, which renders `OAuthCallbackBridge`: it signals the
 * original tab (BroadcastChannel + window.opener) and closes itself. The
 * original tab runs `useIntegrationOAuthListener` and, on the signal, returns
 * the user to the provider overview. Fallbacks keep the same-tab flow working.
 */

import { useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/components/ui/Toast'

export const OAUTH_CHANNEL = 'integration-oauth'
/**
 * Delay before the bridge closes its tab. window.close() destroys the tab
 * synchronously, which would drop the not-yet-delivered async broadcast — so we
 * post first, wait this long for delivery, then close.
 */
export const MESSAGE_FLUSH_MS = 200
/** How long the bridge waits before navigating in place (same-tab fallback). */
export const CLOSE_FALLBACK_MS = 900

export interface OAuthBridgeMessage {
  status: 'connected' | 'error'
  oauthProviderId?: string
  platformConnectorId?: string
  /** Accounting grants need organisation matching; other categories return to
   * the provider because their selected scope is already authoritative. */
  requiresOrganisationReview?: boolean
  reason?: string
}

export function oauthDestinationPath(message: OAuthBridgeMessage): string {
  if (message.status !== 'connected' || !message.platformConnectorId) {
    return '/integrations'
  }
  const suffix = message.requiresOrganisationReview === false ? '' : '/discovery'
  return `/integrations/providers/${message.platformConnectorId}${suffix}`
}

export function openOAuthBroadcast(): BroadcastChannel | null {
  try {
    return new BroadcastChannel(OAUTH_CHANNEL)
  } catch {
    return null
  }
}

/**
 * localStorage key the bridge writes. The `storage` event is the most reliable
 * cross-tab signal: unlike `window.opener` it survives the cross-origin OAuth
 * round-trip (which can sever the opener), and unlike BroadcastChannel it works
 * even when that API is flaky. Belt-and-suspenders alongside the other two.
 */
export const OAUTH_RESULT_KEY = 'integration-oauth-result'
const DIRECT_CONNECTION_WAIT_MS = 2 * 60 * 1000
const DIRECT_CONNECTION_REFRESH_ATTEMPTS = 20
const DIRECT_CONNECTION_REFRESH_DELAY_MS = 500
export const OAUTH_RESULT_POLL_MS = 1000

interface ConnectionsQueryData {
  integrationConnections?: Array<{
    platform_connector_id?: string | null
    external_account_id?: string | null
  }>
}

export function isAwaitingDirectConnection(
  platformConnectorId: string,
  now = Date.now(),
): boolean {
  try {
    const stored = JSON.parse(localStorage.getItem(OAUTH_RESULT_KEY) ?? 'null') as
      | (OAuthBridgeMessage & { t?: number })
      | null
    return Boolean(
      stored?.status === 'connected' &&
        stored.platformConnectorId === platformConnectorId &&
        stored.requiresOrganisationReview === false &&
        typeof stored.t === 'number' &&
        now - stored.t < DIRECT_CONNECTION_WAIT_MS,
    )
  } catch {
    return false
  }
}

/** Write the result to localStorage so every other same-origin tab gets a
 *  `storage` event. The timestamp makes repeated identical results still fire. */
export function writeOAuthResultToStorage(msg: OAuthBridgeMessage): void {
  try {
    localStorage.setItem(OAUTH_RESULT_KEY, JSON.stringify({ ...msg, t: Date.now() }))
  } catch {
    // storage unavailable — the other channels still apply.
  }
}

function readOAuthResultFromStorage():
  | (OAuthBridgeMessage & { t?: number })
  | null {
  try {
    return JSON.parse(localStorage.getItem(OAUTH_RESULT_KEY) ?? 'null') as
      | (OAuthBridgeMessage & { t?: number })
      | null
  } catch {
    return null
  }
}

function hasDirectConnectionInCache(
  queryClient: QueryClient,
  platformConnectorId: string,
): boolean {
  return queryClient
    .getQueriesData<ConnectionsQueryData>({
      queryKey: ['integrations', 'connectionsList'],
    })
    .some(([, data]) =>
      data?.integrationConnections?.some(
        (connection) =>
          connection.platform_connector_id === platformConnectorId &&
          Boolean(connection.external_account_id),
      ),
    )
}

/**
 * OAuth persistence and the callback redirect are separate requests. A callback
 * can reach the browser a few milliseconds before the new connection is
 * visible to the list query, so a single invalidation can cache the old empty
 * response. Direct-scope integrations retry the exact active query until that
 * connection appears; accounting discovery continues using one normal refresh.
 */
export async function refreshIntegrationQueriesAfterOAuth(
  queryClient: QueryClient,
  message: OAuthBridgeMessage,
  options: { attempts?: number; delayMs?: number } = {},
): Promise<void> {
  await queryClient.invalidateQueries({
    queryKey: ['integrations'],
    refetchType: 'none',
  })

  const directConnectorId =
    message.status === 'connected' &&
    message.requiresOrganisationReview === false
      ? message.platformConnectorId
      : undefined
  if (!directConnectorId) {
    await queryClient.refetchQueries({
      queryKey: ['integrations'],
      type: 'active',
    })
    return
  }

  const attempts = options.attempts ?? DIRECT_CONNECTION_REFRESH_ATTEMPTS
  const delayMs = options.delayMs ?? DIRECT_CONNECTION_REFRESH_DELAY_MS
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    await queryClient.refetchQueries({
      queryKey: ['integrations', 'connectionsList'],
      type: 'active',
    })
    if (hasDirectConnectionInCache(queryClient, directConnectorId)) return
    if (attempt + 1 < attempts) {
      await new Promise((resolve) => window.setTimeout(resolve, delayMs))
    }
  }
}

/**
 * Subscribe (in the original tab) to OAuth bridge results across ALL channels:
 * BroadcastChannel, window.opener postMessage, and localStorage `storage`
 * events. De-dupes identical results that arrive on more than one channel
 * within a short window. Returns an unsubscribe fn.
 */
export function subscribeOAuthResult(
  handler: (m: OAuthBridgeMessage) => void,
): () => void {
  let lastKey = ''
  let lastAt = 0
  // The callback can finish while this tab is backgrounded. Some embedded
  // browsers suppress BroadcastChannel, `storage`, and opener messages in that
  // state, so remember the marker that existed when the listener mounted and
  // converge by reading localStorage as well. This is deliberately provider-
  // agnostic: every OAuth callback writes the same timestamped result marker.
  let lastStoredAt = readOAuthResultFromStorage()?.t ?? 0
  const onResult = (m: OAuthBridgeMessage | undefined) => {
    if (!m?.status) return
    const key = `${m.status}:${m.platformConnectorId ?? ''}:${m.oauthProviderId ?? ''}:${String(m.requiresOrganisationReview)}:${m.reason ?? ''}`
    const now = Date.now()
    if (key === lastKey && now - lastAt < 3000) return // same event on another channel
    lastKey = key
    lastAt = now
    handler(m)
  }

  const channel = openOAuthBroadcast()
  if (channel) {
    channel.onmessage = (e: MessageEvent<OAuthBridgeMessage>) => onResult(e.data)
  }
  const onWindowMessage = (e: MessageEvent) => {
    if (e.origin !== window.location.origin) return
    const data = e.data as (OAuthBridgeMessage & { __integrationOAuth?: boolean }) | undefined
    if (data?.__integrationOAuth) onResult(data)
  }
  const onStorage = (e: StorageEvent) => {
    if (e.key !== OAUTH_RESULT_KEY || !e.newValue) return
    try {
      const stored = JSON.parse(e.newValue) as OAuthBridgeMessage & { t?: number }
      if (typeof stored.t === 'number') lastStoredAt = stored.t
      onResult(stored)
    } catch {
      /* malformed — ignore */
    }
  }
  const pollStoredResult = () => {
    const stored = readOAuthResultFromStorage()
    if (!stored || typeof stored.t !== 'number' || stored.t <= lastStoredAt) return
    lastStoredAt = stored.t
    onResult(stored)
  }
  const storagePoll = window.setInterval(pollStoredResult, OAUTH_RESULT_POLL_MS)
  const onFocus = () => pollStoredResult()
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') pollStoredResult()
  }
  window.addEventListener('message', onWindowMessage)
  window.addEventListener('storage', onStorage)
  window.addEventListener('focus', onFocus)
  document.addEventListener('visibilitychange', onVisibilityChange)
  return () => {
    channel?.close()
    window.clearInterval(storagePoll)
    window.removeEventListener('message', onWindowMessage)
    window.removeEventListener('storage', onStorage)
    window.removeEventListener('focus', onFocus)
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }
}

export function oauthSuccessToast(requiresOrganisationReview = true): void {
  toast.success(
    requiresOrganisationReview
      ? 'Connected — review the discovered organisations.'
      : 'Connected — choose how far back to sync.',
  )
}

export function oauthErrorToast(reason?: string): void {
  toast.error(reason ? `Connection failed: ${reason}` : 'Connection failed.')
}

/**
 * Listens (in the original tab) for the OAuth bridge signal — via both
 * BroadcastChannel and window.opener postMessage — and returns the user to the
 * provider overview once consent completes in the other tab (ENT-160).
 */
export function useIntegrationOAuthListener(): void {
  const navigate = useNavigate()
  const qc = useQueryClient()

  useEffect(() => {
    return subscribeOAuthResult((m) => {
      if (m.status === 'connected' && m.platformConnectorId) {
        const requiresReview = m.requiresOrganisationReview !== false
        oauthSuccessToast(requiresReview)
        if (requiresReview) {
          void navigate({
            to: '/integrations/providers/$providerId/discovery',
            params: { providerId: m.platformConnectorId },
          })
        } else {
          void navigate({
            to: '/integrations/providers/$providerId',
            params: { providerId: m.platformConnectorId },
          })
        }
        void refreshIntegrationQueriesAfterOAuth(qc, m)
      } else if (m.status === 'error') {
        oauthErrorToast(m.reason)
      }
    })
  }, [navigate, qc])
}
